/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DeviceEventEmitter,
  NativeEventEmitter,
  NativeModules,
} from 'react-native';
import { useLogger } from '@airasia-rn/core-logger';
import {
  flattenRecentSearchesForHistory,
  useRecentSearches as useFlightRecentSearches,
} from '@airasia-rn/flights/hooks';
import {
  flattenHotelRecentSearchesForHistory,
  useHotelRecentSearches,
} from '@airasia-rn/hotels/hooks';
import {
  flattenSnapRecentSearchesForHistory,
  useSnapRecentSearchesQuery,
} from '@airasia-rn/snap/hooks';
import { SearchHistory } from '../services/types';
import { socialKeys } from './useSocialFeed';

const SCREEN_FOCUS_EVENT = 'screenFocus';
const HOME_SCREEN_NAME = 'tabView';

const useRefetchOnScreenFocus = (refetch: () => void) => {
  useEffect(() => {
    const nativeEventModule = NativeModules.NativeEvent;
    const emitter = nativeEventModule
      ? new NativeEventEmitter(nativeEventModule)
      : DeviceEventEmitter;

    const subscription = emitter.addListener(
      SCREEN_FOCUS_EVENT,
      (payload?: { screen?: string }) => {
        if (payload?.screen === HOME_SCREEN_NAME) {
          refetch();
        }
      },
    );

    return () => subscription.remove();
  }, [refetch]);
};

export type UseSearchHistoryResult = {
  data: SearchHistory | undefined;
  isPending: boolean;
  refetch: () => void;
};

export const useSearchHistory = (): UseSearchHistoryResult => {
  const { logError } = useLogger();

  const flightStoreQuery = useFlightRecentSearches();

  const hasNativeSearchHistoryBridge = Boolean(
    NativeModules.ReactToNative?.getRecentSearches,
  );

  const hotelStoreQuery = useHotelRecentSearches();
  const snapStoreQuery = useSnapRecentSearchesQuery();

  const localSearchHistory = useMemo<SearchHistory>(
    () => ({
      flight: flattenRecentSearchesForHistory(flightStoreQuery.data),
      hotel: flattenHotelRecentSearchesForHistory(hotelStoreQuery.data),
      snap: flattenSnapRecentSearchesForHistory(snapStoreQuery.data),
    }),
    [flightStoreQuery.data, hotelStoreQuery.data, snapStoreQuery.data],
  );

  const query = useQuery({
    queryKey: [...socialKeys.feedPrefix(), 'searchHistory'],
    queryFn: async (): Promise<SearchHistory> => {
      try {
        return await new Promise<SearchHistory>((resolve) => {
          NativeModules.ReactToNative.getRecentSearches(
            ['snap', 'hotel', 'flight'],
            (result?: SearchHistory | null) => resolve(result ?? {}),
          );
        });
      } catch (error) {
        logError(error instanceof Error ? error : new Error(String(error)), {
          context: 'useSearchHistory General',
        });
        return {};
      }
    },
    enabled: hasNativeSearchHistoryBridge,
    staleTime: 1000 * 30,
    refetchInterval: 1000 * 60 * 2,
  });

  useRefetchOnScreenFocus(query.refetch);

  const noopRefetch = useCallback(() => {}, []);

  return hasNativeSearchHistoryBridge
    ? { data: query.data, isPending: query.isPending, refetch: query.refetch }
    : { data: localSearchHistory, isPending: false, refetch: noopRefetch };
};
